import { ChangeEvent, FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { ClubProfile, UpsertMyClubProfilePayload, clubsBookingApi } from '@/api/clubs-booking';
import { auth } from '@/store/authStore';
import AdminShell from '@/components/admin/AdminShell';
import { useUploadBlob } from '../hooks/use-file-upload.hook';
import { getClubSiteUrl } from '@/utils/publicSite';

const emptyProfile: Partial<ClubProfile> = {
  clubName: '',
  description: '',
  contactEmail: '',
  contactPhone: '',
  publicSlug: '',
  bookingTerms: '',
  logoUrl: '',
  showClubTitle: true,
  showInPublicList: false,
};

const ClubProfilePage = () => {
  const ownerName = auth.getUser().name || 'Club owner';
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [profile, setProfile] = useState<Partial<ClubProfile>>(emptyProfile);
  const [savedPublicSlug, setSavedPublicSlug] = useState('');
  const logoInputRef = useRef<HTMLInputElement>(null);
  const { upload, loading: logoUploading } = useUploadBlob();

  const publicClubPageUrl = useMemo(() => {
    const slug = savedPublicSlug.trim();
    if (!slug) return '';
    return getClubSiteUrl(slug);
  }, [savedPublicSlug]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');

      try {
        const profileData = await clubsBookingApi.getMyClubProfile();
        setSavedPublicSlug(profileData?.publicSlug?.trim() || '');
        setProfile(
          profileData
            ? { ...profileData, showClubTitle: profileData.showClubTitle !== false }
            : {
                ...emptyProfile,
                publicSlug: ownerName.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30),
              },
        );
      } catch (err: any) {
        const messageText = err?.response?.data?.message;
        setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Failed to load club profile');
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [ownerName]);

  const saveProfile = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setSavingProfile(true);

    try {
      const payload: UpsertMyClubProfilePayload = {
        clubName: profile.clubName,
        description: profile.description,
        contactEmail: profile.contactEmail,
        contactPhone: profile.contactPhone,
        websiteUrl: profile.websiteUrl,
        addressLine1: profile.addressLine1,
        addressLine2: profile.addressLine2,
        city: profile.city,
        postcode: profile.postcode,
        country: profile.country,
        logoUrl: profile.logoUrl,
        bookingTerms: profile.bookingTerms,
        showClubTitle: profile.showClubTitle !== false,
        showInPublicList: profile.showInPublicList === true,
        publicSlug: profile.publicSlug || '',
      };
      const data = await clubsBookingApi.upsertMyClubProfile(payload);
      setProfile({ ...data, showClubTitle: data.showClubTitle !== false });
      setSavedPublicSlug(data.publicSlug || '');
      setMessage('Club profile saved.');
    } catch (err: any) {
      const messageText = err?.response?.data?.message;
      setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const uploadLogoToTemporaryMedia = async (file: File) => {
    return new Promise<string>((resolve, reject) => {
      let resolved = false;
      upload(file, (url) => {
        resolved = true;
        resolve(url);
      });

      setTimeout(() => {
        if (!resolved) reject(new Error('Upload timed out'));
      }, 30000);
    });
  };

  const onLogoFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file for your club logo.');
      if (logoInputRef.current) logoInputRef.current.value = '';
      return;
    }

    setError('');
    setMessage('');
    try {
      const temporaryLogoUrl = await uploadLogoToTemporaryMedia(file);
      setProfile((prev) => ({ ...prev, logoUrl: temporaryLogoUrl }));
      setMessage('Logo uploaded. Save profile to publish it on your public booking pages.');
    } catch {
      setError('Could not upload logo. Please try again.');
    } finally {
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  return (
    <AdminShell title="Club Profile" subtitle="Manage your branding and public club page details">
      {loading && <p className="mb-4 text-sm text-slate-500">Loading club profile...</p>}
      {(error || message) && (
        <div
          className={`mb-4 rounded-lg border px-4 py-3 text-sm ${
            error
              ? 'border-red-200 bg-red-50 text-red-700'
              : 'border-emerald-200 bg-emerald-50 text-emerald-700'
          }`}
        >
          {error || message}
        </div>
      )}

      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="rounded-xl border border-[#D8F1E2] bg-[#F6FAF7] p-4">
          <p className="text-sm font-medium text-slate-700">Public club page</p>
          <p className="mt-1 text-xs text-slate-500">
            Share one link that shows all published events with book-now options.
          </p>
          {!publicClubPageUrl && (
            <p className="mt-2 text-xs text-slate-500">Save your profile to publish and open this page.</p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input
              readOnly
              value={publicClubPageUrl}
              className="min-w-[260px] flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
              placeholder="Save your site name to generate this URL"
            />
            <button
              type="button"
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
              disabled={!publicClubPageUrl}
              onClick={() => void navigator.clipboard.writeText(publicClubPageUrl)}
            >
              Copy link
            </button>
            <a
              href={publicClubPageUrl || '#'}
              target="_blank"
              rel="noreferrer"
              className={`rounded-lg px-3 py-2 text-sm font-medium ${
                publicClubPageUrl
                  ? 'bg-[#1F8F5F] text-white hover:bg-[#1F8F5F]'
                  : 'pointer-events-none bg-slate-200 text-slate-500'
              }`}
            >
              Open page
            </a>
          </div>
        </div>

        <form onSubmit={saveProfile} className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <p className="text-sm text-slate-600">Club logo</p>
            <p className="mt-1 text-xs text-slate-500">Shown on your public club and booking pages.</p>
            <div className="mt-3 flex flex-wrap items-center gap-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white">
                {profile.logoUrl ? (
                  <img src={profile.logoUrl} alt="Club logo preview" className="h-full w-full object-cover" />
                ) : (
                  <span className="text-xs text-slate-500">No logo</span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => void onLogoFileChange(e)}
                />
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-60"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={logoUploading}
                >
                  {logoUploading ? 'Uploading...' : profile.logoUrl ? 'Replace logo' : 'Upload logo'}
                </button>
                {profile.logoUrl && (
                  <button
                    type="button"
                    className="rounded-lg border border-red-200 bg-white px-3 py-2 text-sm text-red-700 hover:bg-red-50"
                    onClick={() => setProfile((prev) => ({ ...prev, logoUrl: '' }))}
                    disabled={logoUploading}
                  >
                    Remove logo
                  </button>
                )}
              </div>
            </div>
          </div>

          <label className="text-sm text-slate-600">
            Club name
            <input
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-base"
              value={profile.clubName || ''}
              onChange={(e) => setProfile((p) => ({ ...p, clubName: e.target.value }))}
              required
            />
          </label>

          <label className="text-sm text-slate-600">
            Site name
            <input
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-base"
              value={profile.publicSlug || ''}
              onChange={(e) => setProfile((p) => ({ ...p, publicSlug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
              placeholder="e.g. liams-club"
              required
            />
            <p className="mt-1 text-xs text-slate-500">This becomes your subdomain: {profile.publicSlug || 'your-site'}.sessionmonkey.com</p>
          </label>

          <label className="text-sm text-slate-600">
            Contact email
            <input
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-base"
              type="email"
              value={profile.contactEmail || ''}
              onChange={(e) => setProfile((p) => ({ ...p, contactEmail: e.target.value }))}
            />
          </label>

          <label className="text-sm text-slate-600">
            Contact phone
            <input
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-base"
              value={profile.contactPhone || ''}
              onChange={(e) => setProfile((p) => ({ ...p, contactPhone: e.target.value }))}
            />
          </label>

          <label className="text-sm text-slate-600 md:col-span-2">
            Club description
            <textarea
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-base"
              rows={2}
              value={profile.description || ''}
              onChange={(e) => setProfile((p) => ({ ...p, description: e.target.value }))}
            />
          </label>

          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 md:col-span-2">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-[#1F8F5F] focus:ring-[#1F8F5F]"
              checked={profile.showClubTitle !== false}
              onChange={(e) => setProfile((p) => ({ ...p, showClubTitle: e.target.checked }))}
            />
            Show club name next to logo on public pages
          </label>

          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 md:col-span-2">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-[#1F8F5F] focus:ring-[#1F8F5F]"
              checked={profile.showInPublicList === true}
              onChange={(e) => setProfile((p) => ({ ...p, showInPublicList: e.target.checked }))}
            />
            Show this club in the public clubs list
          </label>

          <label className="text-sm text-slate-600 md:col-span-2">
            Booking terms
            <textarea
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-base"
              rows={2}
              value={profile.bookingTerms || ''}
              onChange={(e) => setProfile((p) => ({ ...p, bookingTerms: e.target.value }))}
            />
          </label>

          <div className="md:col-span-2">
            <button
              className="rounded-lg bg-[#1F8F5F] px-5 py-2.5 font-medium text-white hover:bg-[#1F8F5F] disabled:opacity-60"
              disabled={savingProfile}
            >
              {savingProfile ? 'Saving...' : 'Save profile'}
            </button>
          </div>
        </form>
      </section>
    </AdminShell>
  );
};

export default ClubProfilePage;
